<template>
    <div>
        <h3 class="my-2.5 md:mb-0 md:bg-white md:p-5 md:pb-0 rounded-t-lg lg:px-7">
            申請電子通知單
        </h3>
        <div class="white-box md:mb-5 pb-12 md:pb-6">
            <transition
                name="opacity"
                mode="out-in"
            >
                <Skeletons v-if="isGettingData" />
                <div v-else>
                    <div class="mb-5">
                        <h4 class="mb-2.5">
                            請輸入欲變更之內容
                        </h4>
                        <div>
                            <Radio
                                v-if="!showData.applyStatus"
                                v-model:checkedValue="applyStatus"
                                value="true"
                                name="apply"
                                class="items-baseline"
                            >
                                <span>
                                    申請以您為要保人所投保之所有個人險有效及停效保單
                                    <br>
                                    申請後即取消紙本/單據寄發，改以電子郵件 (E-mail) 方式通知。
                                </span>
                            </Radio>
                            <Radio
                                v-if="showData.applyStatus"
                                v-model:checkedValue="applyStatus"
                                value="false"
                                name="apply"
                                class="items-baseline"
                            >
                                <span>
                                    終止以您為要保人所投保之所有個人險有效及停效保單
                                    <br>
                                    知悉並同意，終止後即改以書面方式進行通知。
                                </span>
                            </Radio>
                        </div>
                    </div>
                    <div>
                        <EditTelAndMailInput
                            v-model:validInput="validInput"
                            :info="info"
                            :mobileCodeList="mobileCodeList"
                            :catchErrorArray="catchErrorArray"
                            :editable="false"
                            @getValidResult="getValidResult"
                            @clearError="catchErrorArray = []"
                        />
                    </div>
                </div>
            </transition>
        </div>
        <Bottombar>
            <template #right>
                <ButtonNextService
                    v-model:isSubmitOk="isSubmitOk"
                    :disabled="!applyStatus"
                    @click="validInput = true"
                />
            </template>
        </Bottombar>
        <PopupBase
            v-model:visiblePopup="visiblePopup"
            :params="{
                title: '重要提醒',
                confirmButton: '確認',
                cancelButton: '取消',
                isDisabledButton: !isValid && needEmailVerify,
            }"
            @cancel="visiblePopup= false"
            @confirm="confirmRemind()"
        >
            <template #content>
                <div class="mb-5 text-green-600">
                    <p class="w-full break-all">
                        <span v-if="submitData.mobileCountry && submitData.mobileCountry !== '886'">
                            {{ `(+${submitData.mobileCountry} )` }}
                        </span> {{ submitData.mobile }}
                    </p>
                    <p class="w-full break-all py-2">
                        {{ submitData.email }}
                    </p>
                    <ValidEmailButton
                        v-model:propsValidEmail="propsValidEmail"
                        v-model:isValid="isValid"
                        :needEmailVerify="needEmailVerify"
                        :email="submitData.email"
                    />
                </div>
                <div class="text-error">
                    本次申請變更之要保人行動電話及E-mail，將同時更新以您為要保人於所投保之所有個人險有效及停效保單。
                </div>
                <p class="pt-2 error-msg">
                    {{ errorMessage }}
                </p>
            </template>
        </PopupBase>
        <ValidEmail
            v-model:isValid="isValid"
            v-model:needEmailVerify="needEmailVerify"
            v-model:propsValidEmail="propsValidEmail"
            :inPopupBase="true"
            :email="submitData.email"
        />
        <Loading :is-loading="isLoading" />
    </div>
</template>

<script>
import { reactive, toRefs, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { getInfoMultiENotify, submitMultiENotify } from '@/apis/aplCatlMultiENotify';
import { getMobileCodeInfo } from '@/apis/system';
import Loading from '@/components/Loading.vue';
import Radio from '@/components/radios/Radio.vue';
import Bottombar from '@/components/layout/Bottombar.vue';
import Skeletons from './Skeletons.vue';
import ButtonNextService from '@/components/buttons/ButtonNextService.vue';
import EditTelAndMailInput from '../../editTelAndMail/InputForm.vue';
import PopupBase from '@/components/dialogs/Popup.vue';
import ValidEmail from '@/components/dialogs/ValidEmail.vue';
import ValidEmailButton from '@/components/buttons/ValidEmailButton.vue';
import _ from 'lodash';

export default {
    components: {
        Radio,
        Bottombar,
        Skeletons,
        ButtonNextService,
        EditTelAndMailInput,
        PopupBase,
        Loading,
        ValidEmail,
        ValidEmailButton,
    },
    setup() {
        const route = useRoute();
        const store = useStore();
        const { code } = route.meta;
        const state = reactive({
            isGettingData: false,
            isLoading: false,
            validInput: false,
            visiblePopup: false,
            isSubmitOk: false,
            isValid: false,
            needEmailVerify: true,
            propsValidEmail: false,
        });
        const data = reactive({
            info: {},
            showData: {},
            applyStatus: '',
            submitData: {},
            mobileCodeList: [],
            catchErrorArray: [],
            errorMessage: '',
        });

        const result = computed(() => store.state.services.changeItemsResult[code]);

        const getData = async () => {
            state.isGettingData = true;
            await getInfoMultiENotify().then(res => {
                data.info.mobileCountry = res.data.mobileCountry;
                data.info.mobile = res.data.mobile;
                data.info.email = res.data.email;
                data.showData = _.cloneDeep(res.data);
            }).catch(() => {
                state.isGettingData = false;
            });

            getMobileCodeInfo().then(res => {
                state.isGettingData = false;
                data.mobileCodeList = res.data.mobileCodeList.map(i => {
                    i.label = `${i.text}(+${i.value})`;
                    return i;
                });
            }).catch(() => {
                state.isGettingData = false;
            });
        };

        // store有資料就不打API，直接塞資料
        if (result.value) {
            const { showData, info, applyStatus, mobileCodeList } = result.value;
            data.showData = showData;
            data.info = info;
            data.applyStatus = applyStatus;
            data.mobileCodeList = mobileCodeList;
        } else {
            getData();
        }

        const getValidResult = (returnObj) => {
            const { isPass, returnData } = returnObj;
            if (isPass) {
                data.submitData = returnData;
                if (data.applyStatus === 'true') {
                    state.visiblePopup = true;
                } else {
                    submit(data.submitData);
                }
            }
        };

        const submit = (returnData) => {
            state.isLoading = true;
            const submitData = { ...returnData };
            submitData.applyStatus = data.applyStatus;
            submitMultiENotify(submitData).then(res => {
                const storeData = {
                    showData: data.showData,
                    info: returnData,
                    applyStatus: data.applyStatus,
                    mobileCodeList: data.mobileCodeList
                };
                store.dispatch('services/setChangeItemsResult', { code, value: storeData });
                state.isSubmitOk = true;
            }).catch(error => {
                state.isLoading = false;
                data.catchErrorArray = error.data.details;
            });
        };

        const confirmRemind = () => {
            if (!state.isValid && state.needEmailVerify) {
                data.errorMessage = '請先進行驗證Email';
                return;
            }
            state.visiblePopup = false;
            submit(data.submitData);
        };

        return {
            ...toRefs(state),
            ...toRefs(data),
            getValidResult,
            confirmRemind
        };
    },
};
</script>

<style>
</style>
